import {
  apiDecorator,
  ApiDecoratorOptionsSchema,
} from '@tdp/common/apiFetchDecorator';
import {
  authFetchDecorator,
  AuthDecoratorOptionsSchema,
} from '@tdp/common/auth';
import { AuthenticatedImageFetcher } from '@tdp/common/authenticatedImages';
import { TdpAzureAdAppService } from '@tdp/common/azureAdApp';
import { UserClientPreferenceSchema } from '@tdp/common/clientSwitch';
import { HeaderDataService } from '@tdp/common/header';
import { HostType, ITdpClientHost } from '@tdp/common/host';
import { detectHost } from '@tdp/common/host/hostDetection';
import { ApiLocalizationFetcher } from '@tdp/common/localization';
import { SettingsService } from '@tdp/common/settings';
import { PromisedReturnType } from '@tdp/common/shared/tsUtils';
import { composeLogging, ILogger, LoggingConfigSchema } from '@tdp/common/telemetry';
import { ThemePreferenceSchema } from '@tdp/common/theme';
import { LocalStorageUserPreferenceStore } from '@tdp/common/userPreferences';
import { TdpAdaptiveCardService } from '@tdp/modules/adaptiveCards/redux/adaptiveCardService';
import { TdpAppDetailsService, AnalyticsService, TdpAppValidationService, TdpAppListService } from '@tdp/modules/apps';
import { TdpPackageEditorService } from '@tdp/modules/apps/packageEditor/redux/packageEditorService';
import { TdpPeoplePickerService } from '@tdp/modules/apps/peoplePicker/peoplePickerService';
import { TdpBotService, TdpBotEventSubscriptionService } from '@tdp/modules/bots';

import { BuildConfig } from './util/buildConfig';

async function loadHost({ config, logger: baseLogger }: { config: BuildConfig, logger: ILogger }): Promise<ITdpClientHost> {
  const hostType = detectHost();
  if (hostType === HostType.TEAMS) {
    const { createHost } = await import('@tdp/common/host/teams');
    return await createHost({
      config,
      logger: baseLogger.extend('teamsHost'),
    });
  } else {
    const { createHost } = await import('@tdp/common/host/web');
    return await createHost({
      config,
      logger: baseLogger.extend('webHost'),
      loginHintProvider: () => Promise.resolve(undefined),
    });
  }
}

export async function composeServices(config: BuildConfig) {
  const loggingConfig = LoggingConfigSchema.parse(config.telemetry);
  const { baseLogger } = composeLogging(loggingConfig);
  const startupLogger = baseLogger.extend('services');
  startupLogger.trace('EnvConfig %o', config);

  const { auth, themeReader } = await loadHost({ config, logger: baseLogger });

  const tdpApiDecoratorConfig = ApiDecoratorOptionsSchema.parse(config.api);
  const tdpApiDecorator = apiDecorator(
    { logger: baseLogger.extend('tdp-api') },
    tdpApiDecoratorConfig
  );

  const tdpApiAuthDecoratorConfig = AuthDecoratorOptionsSchema.parse(
    config.api
  );

  const tdpApiAuthDecorator = authFetchDecorator(
    auth,
    tdpApiAuthDecoratorConfig
  );
  const tdpApiFetch = tdpApiAuthDecorator(tdpApiDecorator(fetch));

  // Fetch client for static assets
  const staticsFetchConfig = ApiDecoratorOptionsSchema.parse(config.statics);
  const staticsFetch = apiDecorator(
    { logger: baseLogger.extend('static-fetch') },
    staticsFetchConfig
  )(fetch);

  const appDetailsService = new TdpAppDetailsService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('appDetailsService'),
  });

  const appListService = new TdpAppListService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('appsListService'),
  });

  const analyticsService = new AnalyticsService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('analyticsService'),
  });

  const appValidationService = new TdpAppValidationService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('appValidationService'),
  });

  const packageEditorService = new TdpPackageEditorService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('packageEditorService'),
  });

  const peoplePickerService = new TdpPeoplePickerService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('peoplePickerService'),
  });

  const azureAdAppService = new TdpAzureAdAppService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('tdpAzureAppService'),
  });
  const botService = new TdpBotService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('headerDataReader'),
  });

  const headerDataService = new HeaderDataService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('botReader'),
  });

  const localizationFetcher = new ApiLocalizationFetcher({
    apiFetch: staticsFetch,
    logger: baseLogger.extend('localizationFetcher'),
  });

  const userThemePreference = new LocalStorageUserPreferenceStore({
    storageKey: 'theme',
    schema: ThemePreferenceSchema,
  });

  const userClientPreference = new LocalStorageUserPreferenceStore({
    storageKey: 'client-preference',
    schema: UserClientPreferenceSchema,
  });

  const authenticatedImageFetcher = new AuthenticatedImageFetcher({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('authenticatedImageFetcher'),
  })

  const adaptiveCardService = new TdpAdaptiveCardService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('adaptiveCardService'),
  });

  const settingsService = new SettingsService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('settingsService')
  });

  const botEventSubscriptionService = new TdpBotEventSubscriptionService({
    apiFetch: tdpApiFetch,
    logger: baseLogger.extend('botEventSubscriptionService'),
  });

  return {
    auth,
    themeReader,
    botService,
    logger: baseLogger,
    azureAdAppService,
    localizationFetcher,
    headerDataService,
    appDetailsService,
    userThemePreference,
    userClientPreference,
    appListService,
    analyticsService,
    appValidationService,
    authenticatedImageFetcher,
    packageEditorService,
    peoplePickerService,
    adaptiveCardService,
    settingsService,
    botEventSubscriptionService
  };
}

export type DevPortalServices = PromisedReturnType<typeof composeServices>;
